/* Chapie design picker v6.1 (Oct 12 2026): also follows the variant input itself, so it updates on pages without a product form (VIP "Member's Only"). */
(() => {
  if (customElements.get('chapie-design-picker')) return;

  class ChapieDesignPicker extends HTMLElement {
    constructor() {
      super();
      this.onReady = this.onReady.bind(this);
      this.onVariant = this.onVariant.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onMasterChange = this.onMasterChange.bind(this);
      this.updateArrows = this.updateArrows.bind(this);
      this.filter = null;
    }

    connectedCallback() {
      this.nativePicker = this.closest('variant-picker');
      this.addEventListener('click', this.onClick);
      document.addEventListener('chapie:picker-ready', this.onReady);
      document.addEventListener('variant:change', this.onVariant);
      this.addEventListener('scroll', this.updateArrows, true);
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(this.updateArrows);
        this.observer.observe(this);
      }
      this.initialFrame = requestAnimationFrame(() => {
        if (this.nativePicker?.product) this.initialize();
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('scroll', this.updateArrows, true);
      document.removeEventListener('chapie:picker-ready', this.onReady);
      document.removeEventListener('variant:change', this.onVariant);
      this.observer?.disconnect();
      cancelAnimationFrame(this.initialFrame);
      this.nativePicker?.removeAttribute('data-chapie-ready');
      this.master?.removeEventListener('change', this.onMasterChange);
      this.master = null;
    }

    onReady(event) {
      if (event.target === this.nativePicker) this.initialize();
    }

    initialize() {
      this.records = new Map(Array.from(this.querySelectorAll('template[data-design-record]'))
        .map(template => [String(template.dataset.designRecord), template]));
      if (!this.records.size) return;
      this.designIndex = Number(this.getAttribute('design-index'));
      this.collectionIndex = Number(this.getAttribute('collection-index'));
      this.render(this.nativePicker.selectedVariant);
      // v6.1 (Oct 12 2026): VIP pages for non-members have no product form in the page, so variant:change never
      // reaches the document. Also follow the native picker's variant input, which changes on every selection.
      const master = this.nativePicker.masterSelector;
      if (master && this.master !== master) {
        this.master?.removeEventListener('change', this.onMasterChange);
        this.master = master;
        master.addEventListener('change', this.onMasterChange);
      }
      this.nativePicker.setAttribute('data-chapie-ready', '');
      this.hidden = false;
      requestAnimationFrame(this.updateArrows);
    }

    onVariant(event) {
      if (event.target?.getAttribute?.('id') !== this.getAttribute('form') || !this.records) return;
      this.render(event.detail?.variant);
    }

    onMasterChange() {
      if (this.records) this.render(this.nativePicker.selectedVariant);
    }

    candidates(selected) {
      if (!selected) return [];
      return this.nativePicker.product.variants.filter(variant => this.records.has(String(variant.id)) &&
        (!this.nativePicker.hideSoldOutVariants || variant.available) &&
        variant.options.every((value, index) => index === this.designIndex || index === this.collectionIndex || value === selected.options[index]));
    }

    render(selected) {
      const candidates = this.candidates(selected);
      const signature = candidates.map(v => v.id).join(',');
      this.selected = selected;
      const record = this.records.get(String(selected?.id));
      this.querySelector('[data-selected-design]').textContent = record?.dataset.designName || 'Unavailable';
      const pill = record?.content.querySelector('[data-shipping-pill]');
      this.querySelector('[data-selected-shipping]').replaceChildren(...(pill ? [pill.cloneNode(true)] : []));
      this.querySelector('[data-design-announcement]').textContent = record ? `${record.dataset.designName}. ${pill?.textContent.trim() || ''}` : 'Unavailable';
      if (this.signature !== signature) {
        this.signature = signature;
        this.allVariants = candidates;
        this.groups = new Map();
        candidates.forEach(variant => {
          const group = this.records.get(String(variant.id)).dataset.collection.trim();
          // Untitled designs belong to All only; no synthetic collection pill.
          if (!group) return;
          if (!this.groups.has(group)) this.groups.set(group, []);
          this.groups.get(group).push(variant);
        });
        if (this.filter !== null && !this.groups.has(this.filter)) this.filter = null;
        this.renderDesktop();
        this.renderFilters();
        this.renderMobile();
      }
      this.querySelectorAll('[data-design-variant]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.designVariant === String(selected?.id)));
      });
      requestAnimationFrame(this.updateArrows);
    }

    createTile(variant) {
      const button = this.records.get(String(variant.id)).content.querySelector('[data-design-variant]').cloneNode(true);
      button.setAttribute('aria-pressed', String(variant.id === this.selected?.id));
      if (!variant.available) {
        button.setAttribute('aria-label', `${button.getAttribute('aria-label')}. Sold out`);
        const label = document.createElement('span');
        label.className = 'chapie-design__sold-out';
        label.textContent = 'Sold out';
        button.append(label);
      }
      return button;
    }

    createCarousel(variants, label) {
      const carousel = document.createElement('div');
      carousel.className = 'chapie-design__carousel';
      const track = document.createElement('div');
      track.className = 'chapie-design__track';
      if (variants.length > 4) track.classList.add('chapie-design__track--many');
      track.setAttribute('data-design-track', '');
      track.setAttribute('role', 'group');
      track.setAttribute('aria-label', `${label} designs`);
      variants.forEach(variant => track.append(this.createTile(variant)));
      [-1, 1].forEach(direction => {
        const arrow = document.createElement('button');
        arrow.type = 'button';
        arrow.className = `chapie-design__arrow chapie-design__arrow--${direction < 0 ? 'previous' : 'next'}`;
        arrow.dataset.scrollDirection = String(direction);
        arrow.setAttribute('aria-label', `${direction < 0 ? 'Previous' : 'Next'} ${label} designs`);
        // Static SVG, not merchant content.
        arrow.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="${direction < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        carousel.append(arrow);
      });
      carousel.append(track);
      return carousel;
    }

    visibleVariants() {
      return this.filter === null ? this.allVariants : this.groups.get(this.filter) || [];
    }

    renderDesktop() {
      this.querySelector('[data-desktop-grid]').replaceChildren(...this.visibleVariants().map(variant => this.createTile(variant)));
    }

    renderFilters() {
      const container = this.querySelector('[data-collection-filters]');
      const focused = container.contains(document.activeElement) ? document.activeElement : null;
      const focusAll = focused?.hasAttribute('data-filter-all');
      const focusCollection = focused?.dataset.filterCollection;
      container.replaceChildren();
      [[null, 'All'], ...Array.from(this.groups.keys()).map(title => [title, title.replace(/\s+collection$/i, '')])].forEach(([key, label]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'chapie-design__filter';
        button.textContent = label;
        if (key === null) button.dataset.filterAll = '';
        else button.dataset.filterCollection = key;
        button.setAttribute('aria-pressed', String(this.filter === key));
        container.append(button);
        if (focused && (focusAll ? key === null : key === focusCollection)) button.focus({ preventScroll: true });
      });
    }

    updateFilterState() {
      this.querySelectorAll('[data-collection-filters] button').forEach(button => {
        const key = button.hasAttribute('data-filter-all') ? null : button.dataset.filterCollection;
        button.setAttribute('aria-pressed', String(this.filter === key));
      });
    }

    renderMobile() {
      const variants = this.visibleVariants();
      this.querySelector('[data-mobile-carousel]').replaceChildren(this.createCarousel(variants, this.filter || 'All'));
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-filter-all') || button.hasAttribute('data-filter-collection')) {
        this.filter = button.hasAttribute('data-filter-all') ? null : button.dataset.filterCollection;
        this.updateFilterState();
        this.renderDesktop();
        this.renderMobile();
        requestAnimationFrame(this.updateArrows);
      } else if (button.dataset.designVariant) {
        const variant = this.nativePicker.product.variants.find(item => String(item.id) === button.dataset.designVariant);
        if (!variant) return;
        // Synchronize all option controls before the native master-change
        // handler runs, so Collection-first products cannot select an
        // intermediate combination or emit extra variant events.
        variant.options.forEach((value, index) => {
          const selector = this.nativePicker.optionSelectors[index];
          if (!selector) return;
          selector.querySelectorAll(`[name="option${index + 1}"]`).forEach(input => {
            if (input.type === 'radio') input.checked = input.value === value;
            else if (input.type === 'hidden' || input.tagName === 'SELECT') input.value = value;
          });
        });
        this.nativePicker.selectVariant(variant.id);
      } else if (button.dataset.scrollDirection) {
        const track = button.parentElement.querySelector('[data-design-track]');
        track.scrollBy({ left: Number(button.dataset.scrollDirection) * track.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }

    updateArrows() {
      this.querySelectorAll('[data-design-track]').forEach(track => {
        const overflow = track.scrollWidth > track.clientWidth + 2;
        track.parentElement.querySelectorAll('[data-scroll-direction]').forEach(button => {
          button.hidden = !overflow;
          button.disabled = Number(button.dataset.scrollDirection) < 0 ? track.scrollLeft <= 1 : track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        });
      });
    }
  }

  customElements.define('chapie-design-picker', ChapieDesignPicker);
})();

/* Kept inside every purchase form, including quick-buy and accelerated checkout. */
(() => {
  if (customElements.get('chapie-collection-property')) return;
  class ChapieCollectionProperty extends HTMLElement {
    constructor() {
      super();
      this.onVariant = this.onVariant.bind(this);
      this.sync = this.sync.bind(this);
      this.onFormData = this.onFormData.bind(this);
    }
    connectedCallback() {
      this.form = this.closest('form');
      document.addEventListener('variant:change', this.onVariant);
      this.form?.addEventListener('change', this.sync);
      this.form?.addEventListener('submit', this.sync, true);
      this.form?.addEventListener('formdata', this.onFormData);
      this.initialFrame = requestAnimationFrame(() => this.sync());
    }
    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariant);
      this.form?.removeEventListener('change', this.sync);
      this.form?.removeEventListener('submit', this.sync, true);
      this.form?.removeEventListener('formdata', this.onFormData);
      cancelAnimationFrame(this.initialFrame);
    }
    onVariant(event) {
      if (event.target === this.form) this.setTitle(event.detail?.variant?.id);
    }
    sync(event) {
      // Ignore our own input notification to avoid a recursive change loop.
      if (event?.target?.hasAttribute?.('data-collection-title-input')) return;
      this.setTitle(this.form?.elements.namedItem('id')?.value);
    }
    setTitle(id) {
      const input = this.querySelector('[data-collection-title-input]');
      if (!input) return;
      const record = Array.from(this.querySelectorAll('[data-property-variant]'))
        .find(template => template.dataset.propertyVariant === String(id));
      const title = record?.dataset.collectionTitle || '';
      if (input.value !== title) {
        input.value = title;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    onFormData(event) {
      this.sync();
      event.formData.set('properties[_collection_title]', this.querySelector('[data-collection-title-input]')?.value || '');
    }
  }
  customElements.define('chapie-collection-property', ChapieCollectionProperty);
})();
